import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useProject } from '../contexts/ProjectContext';
import { useSettings } from '../contexts/SettingsContext';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import * as BiIcons from 'react-icons/bi';

const { FiX, FiAlertCircle, FiZap } = FiIcons;
const { BiBot } = BiIcons;

const AIAppCreator = ({ onClose }) => {
  const { createProjectWithFiles } = useProject(); // Use createProjectWithFiles instead
  const { settings } = useSettings();
  const navigate = useNavigate();
  const [projectName, setProjectName] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);

  // Enhanced AI configuration check
  const isAIConfigured = () => {
    if (settings.aiProvider === 'local') {
      const runningModel = localStorage.getItem('runningLocalModel');
      return !!runningModel && !!window.localLLMEngine;
    }
    if (settings.aiProvider === 'openai' || settings.aiProvider === 'anthropic') {
      return settings.aiApiKey && settings.aiApiKey.trim().length > 0;
    }
    if (settings.aiProvider === 'custom') {
      return settings.aiApiKey && settings.customApiUrl && 
             settings.aiApiKey.trim().length > 0 && settings.customApiUrl.trim().length > 0;
    }
    return false;
  };

  const handleCreateApp = async () => {
    if (!projectName.trim() || !description.trim() || !isAIConfigured()) return;

    setLoading(true);

    try {
      // Generate files using AI with enhanced, unique prompt
      const timestamp = Date.now();
      const randomSeed = Math.random().toString(36).substring(7);
      
      const prompt = `Create a completely UNIQUE and CREATIVE web application. Make it different from typical examples.

STRICT REQUIREMENTS:
1. Make this app UNIQUE - avoid generic templates and common examples
2. Use the timestamp ${timestamp} and seed ${randomSeed} to ensure uniqueness
3. Create something specifically tailored to: "${description}"
4. Include innovative features and creative design elements
5. Make it visually stunning with modern CSS animations and gradients
6. Add meaningful JavaScript functionality that matches the app purpose
7. Ensure the app is fully interactive and engaging

TECHNICAL REQUIREMENTS:
- Return ONLY valid JSON in this exact format
- Each file must be complete and functional
- Use modern CSS with creative animations, gradients, and responsive design
- Include proper error handling and user feedback
- Add comments explaining the unique features

Required JSON format:
{
  "index.html": "complete HTML with proper structure, meta tags, and semantic elements",
  "style.css": "modern CSS with unique animations, creative gradients, and responsive design",
  "script.js": "functional JavaScript with innovative features and interactivity"
}

APP DESCRIPTION: ${description}

CREATIVITY REQUIREMENTS:
- Use unique color schemes and design patterns
- Add creative micro-interactions and animations
- Include innovative functionality specific to the description
- Make the UI/UX exceptional and memorable
- Add unique features that haven't been seen in typical examples
- Use creative layouts, typography, and visual effects

Make this app stand out and be truly unique! Avoid any generic templates or common patterns.`;

      console.log('🚀 Calling AI for UNIQUE app generation...');
      const aiResponse = await callAI(prompt);
      console.log('📝 AI Response received:', aiResponse.substring(0, 200) + '...');

      // Parse the AI response to extract files
      let generatedFiles;
      try {
        // Try to extract JSON from the response
        const jsonMatch = aiResponse.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          const jsonString = jsonMatch[0];
          console.log('🔍 Extracted JSON:', jsonString.substring(0, 200) + '...');
          generatedFiles = JSON.parse(jsonString);
          console.log('✅ Successfully parsed JSON files:', Object.keys(generatedFiles));
        } else {
          throw new Error('No JSON found in response');
        }
      } catch (parseError) {
        console.warn('❌ JSON parsing failed:', parseError);
        console.log('Raw AI Response:', aiResponse);
        
        // Enhanced fallback: create truly unique files with AI content
        generatedFiles = {
          'index.html': extractCodeBlock(aiResponse, 'html') || createUniqueHTML(description, projectName, timestamp),
          'style.css': extractCodeBlock(aiResponse, 'css') || createUniqueCSS(description, timestamp),
          'script.js': extractCodeBlock(aiResponse, 'javascript') || extractCodeBlock(aiResponse, 'js') || createUniqueJS(description, timestamp)
        };
      }

      // Clean up the generated files
      const cleanedFiles = {};
      Object.entries(generatedFiles).forEach(([filename, content]) => {
        if (content && typeof content === 'string' && content.trim().length > 0) {
          // Clean up the content (remove any markdown formatting)
          let cleanContent = content
            .replace(/```html\n?/g, '')
            .replace(/```css\n?/g, '')
            .replace(/```javascript\n?/g, '')
            .replace(/```js\n?/g, '')
            .replace(/```\n?/g, '')
            .trim();
          
          cleanedFiles[filename] = cleanContent;
          console.log(`✅ Prepared file ${filename}: ${cleanContent.length} characters`);
        } else {
          console.warn(`⚠️ Skipping invalid file ${filename}:`, content);
        }
      });

      // Ensure we have at least a basic HTML file
      if (!cleanedFiles['index.html'] || cleanedFiles['index.html'].trim().length < 50) {
        console.log('🔧 Creating fallback HTML file...');
        cleanedFiles['index.html'] = createUniqueHTML(description, projectName, timestamp);
      }

      // Create the project with all files using the proper method
      console.log('🚀 Creating project with AI-generated files...');
      const newProject = await createProjectWithFiles(
        projectName.trim(),
        'blank', // template
        cleanedFiles // Pass all cleaned files
      );

      console.log('🎉 AI app creation completed successfully!');
      console.log('📁 Final project files:', Object.keys(newProject.files));

      // Close modal and navigate to editor
      onClose();
      navigate('/editor');

    } catch (error) {
      console.error('❌ Error creating AI app:', error);
      
      // Provide more specific error messages
      let errorMessage = 'Failed to create app with AI: ';
      if (error.message.includes('API')) {
        errorMessage += 'AI API error. Please check your API key and try again.';
      } else if (error.message.includes('network')) {
        errorMessage += 'Network error. Please check your internet connection.';
      } else if (error.message.includes('JSON')) {
        errorMessage += 'AI response parsing error. Please try again with a different description.';
      } else {
        errorMessage += error.message;
      }
      
      alert(`${errorMessage}\n\nPlease try again or check your AI configuration.`);
    } finally {
      setLoading(false);
    }
  };

  const callAI = async (prompt) => {
    console.log('🤖 Calling AI with provider:', settings.aiProvider);

    if (settings.aiProvider === 'local') {
      return await callRealLocalModel(prompt);
    } else if (settings.aiProvider === 'openai') {
      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${settings.aiApiKey}`
        },
        body: JSON.stringify({
          model: 'gpt-3.5-turbo',
          messages: [
            {
              role: 'system',
              content: 'You are a creative web developer and innovative designer. Create amazing, unique web applications that are completely different each time. Never use generic templates. Always be creative and innovative. Each app should be a masterpiece of creativity and functionality.'
            },
            {
              role: 'user',
              content: prompt
            }
          ],
          max_tokens: 4000,
          temperature: 1.0 // Maximum creativity
        })
      });

      if (!response.ok) {
        throw new Error(`OpenAI API Error: ${response.status} - ${response.statusText}`);
      }

      const data = await response.json();
      return data.choices[0].message.content;
    } else if (settings.aiProvider === 'anthropic') {
      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': settings.aiApiKey,
          'anthropic-version': '2023-06-01'
        },
        body: JSON.stringify({
          model: 'claude-3-sonnet-20240229',
          max_tokens: 4000,
          temperature: 1.0,
          messages: [{
            role: 'user',
            content: prompt
          }]
        })
      });

      if (!response.ok) {
        throw new Error(`Anthropic API Error: ${response.status} - ${response.statusText}`);
      }

      const data = await response.json();
      return data.content[0].text;
    } else if (settings.aiProvider === 'custom' && settings.customApiUrl) {
      const response = await fetch(settings.customApiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${settings.aiApiKey}`
        },
        body: JSON.stringify({
          prompt: prompt,
          max_tokens: 4000,
          temperature: 1.0
        })
      });

      if (!response.ok) {
        throw new Error(`Custom API Error: ${response.status} - ${response.statusText}`);
      }

      const data = await response.json();
      return data.response || data.text || data.content || 'No response received';
    } else {
      throw new Error('AI provider not configured properly');
    }
  };

  const callRealLocalModel = async (prompt) => {
    const runningModel = localStorage.getItem('runningLocalModel');
    console.log('🤖 Using REAL local model for unique app creation:', runningModel);

    try {
      // Check if we have the WebLLM engine loaded
      if (!window.localLLMEngine) {
        throw new Error('Local model engine not initialized. Please load a model first in Settings > AI Configuration > Manage Models.');
      }

      console.log('🚀 Using WebLLM engine for creative app generation...');

      // Call the actual WebLLM model with enhanced parameters for creativity
      const response = await window.localLLMEngine.chat.completions.create({
        messages: [
          {
            role: 'system',
            content: 'You are an extremely creative web developer who builds amazing, unique applications. Each app you create should be completely different, visually stunning, highly interactive, and tailored to the specific request. Never use generic templates. Always return valid JSON with complete HTML, CSS, and JavaScript files. Make every app a creative masterpiece!'
          },
          {
            role: 'user',
            content: prompt
          }
        ],
        max_tokens: 4000,
        temperature: 1.0, // Maximum creativity
        top_p: 0.95
      });

      const assistantResponse = response.choices[0].message.content;
      console.log('✅ Real local AI creative app generation response received');
      console.log('📄 Response preview:', assistantResponse.substring(0, 300) + '...');

      return assistantResponse;
    } catch (error) {
      console.error('❌ Real local AI error:', error);

      // Provide helpful error messages
      let errorMessage = 'Local AI Error: ';
      if (error.message.includes('not initialized')) {
        errorMessage += 'Model not loaded. Please go to Settings > AI Configuration > Manage Models and load a model (TinyLlama, RedPajama, or Llama 2).';
      } else if (error.message.includes('memory')) {
        errorMessage += 'Not enough memory. Try closing other browser tabs, reloading the page, or use TinyLlama (smallest model).';
      } else if (error.message.includes('network')) {
        errorMessage += 'Network error. The model might still be downloading or initializing.';
      } else {
        errorMessage += error.message;
      }

      throw new Error(errorMessage);
    }
  };

  // Helper functions for enhanced unique fallbacks
  const extractCodeBlock = (content, language) => {
    const regex = new RegExp(`\`\`\`${language}\\s*\\n([\\s\\S]*?)\\n\`\`\``, 'i');
    const match = content.match(regex);
    return match ? match[1].trim() : null;
  };

  const createUniqueHTML = (desc, projectName, timestamp) => {
    const uniqueId = `app-${timestamp}`;
    const colors = ['#667eea', '#764ba2', '#f093fb', '#f5576c', '#4facfe', '#00f2fe'];
    const randomColor = colors[timestamp % colors.length];

    return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${projectName} - Unique AI Generated</title>
    <link rel="stylesheet" href="style.css">
    <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap" rel="stylesheet">
    <meta name="description" content="${desc}">
    <meta name="generator" content="AI App Creator - Unique Build ${timestamp}">
</head>
<body>
    <div id="${uniqueId}" class="app-container">
        <header class="app-header">
            <div class="header-content">
                <h1 class="app-title" data-text="${projectName}">${projectName}</h1>
                <p class="app-subtitle">Uniquely Generated for: ${desc}</p>
                <div class="header-decoration"></div>
            </div>
        </header>

        <main class="app-main">
            <section class="hero-section">
                <div class="hero-content">
                    <div class="feature-showcase">
                        <div class="feature-card primary" data-feature="main">
                            <div class="feature-icon">🎨</div>
                            <h2>Creative Implementation</h2>
                            <p>${desc}</p>
                            <button class="cta-button" id="primaryAction">Experience It</button>
                        </div>

                        <div class="feature-grid">
                            <div class="feature-item" data-index="1">
                                <span class="feature-number">01</span>
                                <h3>Interactive Design</h3>
                                <p>Unique user experience</p>
                            </div>
                            <div class="feature-item" data-index="2">
                                <span class="feature-number">02</span>
                                <h3>Modern Features</h3>
                                <p>Cutting-edge functionality</p>
                            </div>
                            <div class="feature-item" data-index="3">
                                <span class="feature-number">03</span>
                                <h3>Creative Vision</h3>
                                <p>Innovative approach</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section class="interaction-zone">
                <div class="zone-header">
                    <h2>Interactive Experience</h2>
                    <div class="zone-controls">
                        <button class="control-btn" data-action="create">Create</button>
                        <button class="control-btn" data-action="explore">Explore</button>
                        <button class="control-btn" data-action="discover">Discover</button>
                    </div>
                </div>

                <div class="dynamic-content" id="dynamicArea">
                    <div class="content-placeholder">
                        <div class="placeholder-icon">✨</div>
                        <p>Ready for your unique experience...</p>
                    </div>
                </div>

                <div class="stats-dashboard">
                    <div class="stat-item">
                        <div class="stat-value" id="uniqueCounter">0</div>
                        <div class="stat-label">Interactions</div>
                    </div>
                    <div class="stat-item">
                        <div class="stat-value">${timestamp.toString().slice(-3)}</div>
                        <div class="stat-label">Unique ID</div>
                    </div>
                    <div class="stat-item">
                        <div class="stat-value">100%</div>
                        <div class="stat-label">AI Generated</div>
                    </div>
                </div>
            </section>
        </main>

        <footer class="app-footer">
            <div class="footer-content">
                <p>Uniquely crafted with AI • Build ${timestamp}</p>
                <div class="footer-decoration"></div>
            </div>
        </footer>
    </div>

    <script src="script.js"></script>
</body>
</html>`;
  };

  const createUniqueCSS = (desc, timestamp) => {
    const hue = timestamp % 360;
    const secondaryHue = (timestamp + 180) % 360;

    return `/* Unique AI Generated Styles - Build ${timestamp} */

* {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
}

:root {
    --primary-hue: ${hue};
    --secondary-hue: ${secondaryHue};
    --primary-color: hsl(var(--primary-hue), 70%, 60%);
    --secondary-color: hsl(var(--secondary-hue), 65%, 55%);
    --accent-color: hsl(calc(var(--primary-hue) + 45), 80%, 65%);
    --bg-gradient: linear-gradient(135deg, 
        hsl(var(--primary-hue), 20%, 5%) 0%, 
        hsl(var(--secondary-hue), 15%, 8%) 50%, 
        hsl(calc(var(--primary-hue) + 90), 18%, 6%) 100%);
    --text-primary: #ffffff;
    --text-secondary: rgba(255, 255, 255, 0.8);
    --text-muted: rgba(255, 255, 255, 0.6);
}

body {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    background: var(--bg-gradient);
    color: var(--text-primary);
    min-height: 100vh;
    overflow-x: hidden;
    position: relative;
}

body::before {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: 
        radial-gradient(circle at 20% 20%, var(--primary-color)20 0%, transparent 50%),
        radial-gradient(circle at 80% 80%, var(--secondary-color)15 0%, transparent 50%),
        radial-gradient(circle at 40% 60%, var(--accent-color)10 0%, transparent 50%);
    pointer-events: none;
    z-index: -1;
    animation: backgroundShift 20s ease-in-out infinite;
}

@keyframes backgroundShift {
    0%, 100% { transform: translateX(0) translateY(0) scale(1); }
    33% { transform: translateX(-20px) translateY(-30px) scale(1.1); }
    66% { transform: translateX(20px) translateY(20px) scale(0.95); }
}

.app-container {
    position: relative;
    z-index: 1;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
}

/* Header Styles */
.app-header {
    padding: 2rem 2rem 4rem;
    text-align: center;
    position: relative;
    overflow: hidden;
}

.header-content {
    position: relative;
    z-index: 2;
    max-width: 800px;
    margin: 0 auto;
}

.app-title {
    font-size: clamp(2.5rem, 8vw, 4.5rem);
    font-weight: 700;
    margin-bottom: 1rem;
    background: linear-gradient(135deg, var(--primary-color), var(--accent-color), var(--secondary-color));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    position: relative;
    animation: titleGlow 3s ease-in-out infinite alternate;
}

@keyframes titleGlow {
    0% { filter: drop-shadow(0 0 20px var(--primary-color)40); }
    100% { filter: drop-shadow(0 0 40px var(--accent-color)60); }
}

.app-title::before {
    content: attr(data-text);
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, transparent 30%, var(--accent-color)20 50%, transparent 70%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: shimmer 2s infinite;
}

@keyframes shimmer {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
}

.app-subtitle {
    font-size: 1.2rem;
    color: var(--text-secondary);
    margin-bottom: 2rem;
    font-weight: 300;
    letter-spacing: 0.5px;
}

.header-decoration {
    width: 100px;
    height: 4px;
    background: linear-gradient(90deg, var(--primary-color), var(--accent-color));
    margin: 2rem auto;
    border-radius: 2px;
    animation: decorationPulse 2s ease-in-out infinite alternate;
}

@keyframes decorationPulse {
    0% { transform: scaleX(1); opacity: 0.8; }
    100% { transform: scaleX(1.5); opacity: 1; }
}

/* Main Content */
.app-main {
    flex: 1;
    padding: 0 2rem;
    max-width: 1200px;
    margin: 0 auto;
    width: 100%;
}

.hero-section {
    margin-bottom: 4rem;
}

.feature-showcase {
    display: grid;
    grid-template-columns: 1fr;
    gap: 3rem;
    align-items: start;
}

.feature-card {
    background: rgba(255, 255, 255, 0.05);
    backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 24px;
    padding: 3rem;
    text-align: center;
    position: relative;
    overflow: hidden;
    transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.feature-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.1) 50%, transparent 100%);
    transition: left 0.6s ease;
}

.feature-card:hover::before {
    left: 100%;
}

.feature-card:hover {
    transform: translateY(-10px) scale(1.02);
    border-color: var(--primary-color);
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3), 0 0 0 1px var(--primary-color)30;
}

.feature-icon {
    font-size: 4rem;
    margin-bottom: 1.5rem;
    display: block;
    animation: iconFloat 3s ease-in-out infinite;
}

@keyframes iconFloat {
    0%, 100% { transform: translateY(0) rotate(0deg); }
    50% { transform: translateY(-10px) rotate(5deg); }
}

.feature-card h2 {
    font-size: 2rem;
    margin-bottom: 1rem;
    color: var(--text-primary);
    font-weight: 600;
}

.feature-card p {
    font-size: 1.1rem;
    color: var(--text-secondary);
    line-height: 1.6;
    margin-bottom: 2rem;
}

.cta-button {
    background: linear-gradient(135deg, var(--primary-color), var(--accent-color));
    color: white;
    border: none;
    padding: 1rem 2.5rem;
    border-radius: 50px;
    font-size: 1.1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    text-transform: uppercase;
    letter-spacing: 1px;
    box-shadow: 0 10px 30px var(--primary-color)40;
}

.cta-button:hover {
    transform: translateY(-3px);
    box-shadow: 0 15px 40px var(--primary-color)60;
    background: linear-gradient(135deg, var(--accent-color), var(--secondary-color));
}

.cta-button:active {
    transform: translateY(-1px);
}

/* Feature Grid */
.feature-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1.5rem;
    margin-top: 2rem;
}

.feature-item {
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 16px;
    padding: 1.5rem;
    text-align: center;
    transition: all 0.3s ease;
    cursor: pointer;
}

.feature-item:hover {
    transform: translateY(-5px);
    background: rgba(255, 255, 255, 0.08);
    border-color: var(--accent-color)50;
}

.feature-number {
    display: block;
    font-size: 2rem;
    font-weight: 700;
    color: var(--primary-color);
    margin-bottom: 0.5rem;
}

.feature-item h3 {
    font-size: 1.1rem;
    margin-bottom: 0.5rem;
    color: var(--text-primary);
}

.feature-item p {
    font-size: 0.9rem;
    color: var(--text-muted);
}

/* Interaction Zone */
.interaction-zone {
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 24px;
    padding: 3rem;
    margin-bottom: 4rem;
    backdrop-filter: blur(20px);
}

.zone-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 2rem;
    flex-wrap: wrap;
    gap: 1rem;
}

.zone-header h2 {
    font-size: 2rem;
    color: var(--text-primary);
    font-weight: 600;
}

.zone-controls {
    display: flex;
    gap: 1rem;
}

.control-btn {
    background: rgba(255, 255, 255, 0.1);
    color: var(--text-primary);
    border: 1px solid rgba(255, 255, 255, 0.2);
    padding: 0.8rem 1.5rem;
    border-radius: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
}

.control-btn:hover {
    background: var(--primary-color)20;
    border-color: var(--primary-color);
    transform: translateY(-2px);
}

.dynamic-content {
    min-height: 200px;
    background: rgba(0, 0, 0, 0.2);
    border-radius: 16px;
    padding: 2rem;
    margin-bottom: 2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px dashed rgba(255, 255, 255, 0.1);
    transition: all 0.3s ease;
}

.content-placeholder {
    text-align: center;
    color: var(--text-muted);
}

.placeholder-icon {
    font-size: 3rem;
    margin-bottom: 1rem;
    animation: placeholderPulse 2s ease-in-out infinite;
}

@keyframes placeholderPulse {
    0%, 100% { opacity: 0.5; transform: scale(1); }
    50% { opacity: 1; transform: scale(1.1); }
}

/* Stats Dashboard */
.stats-dashboard {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 1rem;
}

.stat-item {
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    padding: 1.5rem;
    text-align: center;
    transition: all 0.3s ease;
}

.stat-item:hover {
    transform: translateY(-3px);
    background: rgba(255, 255, 255, 0.08);
}

.stat-value {
    font-size: 2rem;
    font-weight: 700;
    color: var(--primary-color);
    margin-bottom: 0.5rem;
}

.stat-label {
    font-size: 0.9rem;
    color: var(--text-muted);
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

/* Footer */
.app-footer {
    text-align: center;
    padding: 2rem;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    margin-top: auto;
}

.footer-content {
    color: var(--text-muted);
    font-size: 0.9rem;
}

.footer-decoration {
    width: 50px;
    height: 2px;
    background: var(--accent-color);
    margin: 1rem auto 0;
    border-radius: 1px;
}

/* Responsive Design */
@media (max-width: 768px) {
    .app-header {
        padding: 1.5rem 1rem 3rem;
    }

    .app-main {
        padding: 0 1rem;
    }

    .feature-card {
        padding: 2rem 1.5rem;
    }

    .interaction-zone {
        padding: 2rem 1.5rem;
    }

    .zone-header {
        flex-direction: column;
        align-items: stretch;
    }

    .zone-controls {
        justify-content: center;
    }

    .control-btn {
        flex: 1;
        text-align: center;
    }
}

@media (min-width: 768px) {
    .feature-showcase {
        grid-template-columns: 2fr 1fr;
        gap: 4rem;
    }
}

/* Custom Animations */
@keyframes fadeInUp {
    from {
        opacity: 0;
        transform: translateY(30px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.feature-card, .interaction-zone {
    animation: fadeInUp 0.8s ease-out;
}

.feature-item:nth-child(1) { animation-delay: 0.1s; }
.feature-item:nth-child(2) { animation-delay: 0.2s; }
.feature-item:nth-child(3) { animation-delay: 0.3s; }`;
  };

  const createUniqueJS = (desc, timestamp) => {
    return `// Unique AI Generated JavaScript - Build ${timestamp}
// Application: ${desc}

class UniqueApp {
    constructor() {
        this.timestamp = ${timestamp};
        this.interactionCount = 0;
        this.isAnimating = false;
        this.uniqueFeatures = [];
        this.dynamicContent = [];
        this.init();
    }

    init() {
        console.log(\`🚀 Unique App Initialized - Build \${this.timestamp}\`);
        this.setupEventListeners();
        this.initializeUniqueFeatures();
        this.startDynamicAnimations();
        this.setupInteractiveElements();
    }

    setupEventListeners() {
        // Primary CTA Button
        const primaryBtn = document.getElementById('primaryAction');
        if (primaryBtn) {
            primaryBtn.addEventListener('click', (e) => this.handlePrimaryAction(e));
        }

        // Control Buttons
        document.querySelectorAll('.control-btn').forEach(btn => {
            btn.addEventListener('click', (e) => this.handleControlAction(e));
        });

        // Feature Items
        document.querySelectorAll('.feature-item').forEach((item, index) => {
            item.addEventListener('click', () => this.activateFeature(index + 1));
        });

        // Keyboard Shortcuts
        document.addEventListener('keydown', (e) => this.handleKeyboard(e));

        // Mouse Movement Effects
        document.addEventListener('mousemove', (e) => this.handleMouseMove(e));

        // Window Resize
        window.addEventListener('resize', () => this.handleResize());
    }

    handlePrimaryAction(e) {
        if (this.isAnimating) return;
        
        this.isAnimating = true;
        this.updateInteractionCount();

        const button = e.target;
        const originalText = button.textContent;

        // Unique animation sequence
        button.style.transform = 'scale(0.95)';
        button.textContent = 'Processing...';

        setTimeout(() => {
            this.createUniqueExperience();
            button.textContent = 'Amazing!';
            button.style.transform = 'scale(1)';

            setTimeout(() => {
                button.textContent = originalText;
                this.isAnimating = false;
            }, 2000);
        }, 500);
    }

    handleControlAction(e) {
        const action = e.target.dataset.action;
        const dynamicArea = document.getElementById('dynamicArea');
        
        this.updateInteractionCount();

        const experiences = {
            create: () => this.generateCreativeContent(dynamicArea),
            explore: () => this.generateExplorationInterface(dynamicArea),
            discover: () => this.generateDiscoveryExperience(dynamicArea)
        };

        if (experiences[action]) {
            experiences[action]();
        }
    }

    generateCreativeContent(container) {
        const uniqueId = \`creative-\${Date.now()}\`;
        const colors = this.generateUniqueColorPalette();

        container.innerHTML = \`
            <div class="creative-workspace" id="\${uniqueId}">
                <h3>Creative Workspace</h3>
                <div class="creation-tools">
                    <button class="tool-btn" data-tool="paint">🎨 Paint</button>
                    <button class="tool-btn" data-tool="shape">📐 Shapes</button>
                    <button class="tool-btn" data-tool="text">✏️ Text</button>
                </div>
                <div class="canvas-area">
                    <canvas id="uniqueCanvas" width="400" height="300"></canvas>
                </div>
                <div class="color-palette">
                    \${colors.map(color => \`<div class="color-swatch" style="background: \${color}" data-color="\${color}"></div>\`).join('')}
                </div>
            </div>
        \`;

        this.initializeCanvas();
    }

    generateExplorationInterface(container) {
        const explorationData = this.generateUniqueData();

        container.innerHTML = \`
            <div class="exploration-hub">
                <h3>Exploration Hub</h3>
                <div class="exploration-grid">
                    \${explorationData.map((item, index) => \`
                        <div class="exploration-card" data-index="\${index}" style="animation-delay: \${index * 0.1}s">
                            <div class="card-icon">\${item.icon}</div>
                            <h4>\${item.title}</h4>
                            <p>\${item.description}</p>
                            <div class="card-progress">
                                <div class="progress-bar" style="width: \${item.progress}%"></div>
                            </div>
                        </div>
                    \`).join('')}
                </div>
            </div>
        \`;

        this.animateExplorationCards();
    }

    generateDiscoveryExperience(container) {
        container.innerHTML = \`
            <div class="discovery-interface">
                <h3>Discovery Mode</h3>
                <div class="discovery-content">
                    <div class="mystery-box" id="mysteryBox">
                        <div class="box-content">
                            <div class="mystery-icon">🎁</div>
                            <p>Click to discover something unique!</p>
                        </div>
                    </div>
                    <div class="discovery-results" id="discoveryResults"></div>
                </div>
            </div>
        \`;

        document.getElementById('mysteryBox').addEventListener('click', () => {
            this.revealDiscovery();
        });
    }

    createUniqueExperience() {
        // Generate unique visual effects
        this.createParticleSystem();
        this.triggerColorWave();
        this.updateDynamicStats();
    }

    createParticleSystem() {
        const particleCount = 20;
        const colors = ['var(--primary-color)', 'var(--secondary-color)', 'var(--accent-color)'];

        for (let i = 0; i < particleCount; i++) {
            setTimeout(() => {
                const particle = document.createElement('div');
                particle.className = 'unique-particle';
                particle.style.cssText = \`
                    position: fixed;
                    width: 6px;
                    height: 6px;
                    background: \${colors[i % colors.length]};
                    border-radius: 50%;
                    pointer-events: none;
                    z-index: 1000;
                    left: \${Math.random() * window.innerWidth}px;
                    top: \${window.innerHeight}px;
                    animation: particleRise \${2 + Math.random() * 3}s ease-out forwards;
                \`;

                document.body.appendChild(particle);

                setTimeout(() => {
                    particle.remove();
                }, 5000);
            }, i * 100);
        }
    }

    triggerColorWave() {
        const elements = document.querySelectorAll('.feature-card, .stat-item, .control-btn');
        
        elements.forEach((el, index) => {
            setTimeout(() => {
                el.style.transform = 'scale(1.05)';
                el.style.filter = 'hue-rotate(45deg)';
                
                setTimeout(() => {
                    el.style.transform = '';
                    el.style.filter = '';
                }, 300);
            }, index * 100);
        });
    }

    updateInteractionCount() {
        this.interactionCount++;
        const counter = document.getElementById('uniqueCounter');
        if (counter) {
            counter.style.transform = 'scale(1.2)';
            counter.textContent = this.interactionCount;
            
            setTimeout(() => {
                counter.style.transform = 'scale(1)';
            }, 200);
        }
    }

    generateUniqueColorPalette() {
        const baseHue = this.timestamp % 360;
        return [
            \`hsl(\${baseHue}, 70%, 60%)\`,
            \`hsl(\${(baseHue + 60) % 360}, 65%, 55%)\`,
            \`hsl(\${(baseHue + 120) % 360}, 75%, 65%)\`,
            \`hsl(\${(baseHue + 180) % 360}, 60%, 50%)\`,
            \`hsl(\${(baseHue + 240) % 360}, 80%, 70%)\`,
            \`hsl(\${(baseHue + 300) % 360}, 55%, 45%)\`
        ];
    }

    generateUniqueData() {
        const icons = ['🚀', '⭐', '🎯', '💎', '🔥', '✨'];
        const titles = ['Innovative Feature', 'Creative Element', 'Unique Function', 'Special Capability', 'Amazing Tool', 'Exclusive Content'];
        
        return Array.from({ length: 6 }, (_, i) => ({
            icon: icons[i],
            title: titles[i],
            description: \`Unique implementation #\${i + 1} for your specific needs\`,
            progress: Math.floor(Math.random() * 100) + 1
        }));
    }

    initializeCanvas() {
        const canvas = document.getElementById('uniqueCanvas');
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        let isDrawing = false;
        let currentColor = 'var(--primary-color)';

        canvas.addEventListener('mousedown', (e) => {
            isDrawing = true;
            ctx.beginPath();
            ctx.moveTo(e.offsetX, e.offsetY);
        });

        canvas.addEventListener('mousemove', (e) => {
            if (!isDrawing) return;
            
            ctx.lineTo(e.offsetX, e.offsetY);
            ctx.strokeStyle = currentColor;
            ctx.lineWidth = 3;
            ctx.lineCap = 'round';
            ctx.stroke();
        });

        canvas.addEventListener('mouseup', () => {
            isDrawing = false;
        });

        // Color palette functionality
        document.querySelectorAll('.color-swatch').forEach(swatch => {
            swatch.addEventListener('click', (e) => {
                currentColor = e.target.dataset.color;
                document.querySelectorAll('.color-swatch').forEach(s => s.classList.remove('active'));
                e.target.classList.add('active');
            });
        });
    }

    animateExplorationCards() {
        const cards = document.querySelectorAll('.exploration-card');
        
        cards.forEach((card, index) => {
            card.addEventListener('click', () => {
                card.style.transform = 'rotateY(180deg)';
                
                setTimeout(() => {
                    card.style.transform = 'rotateY(0deg)';
                    card.style.background = \`linear-gradient(135deg, hsl(\${(this.timestamp + index * 60) % 360}, 50%, 20%), hsl(\${(this.timestamp + index * 60 + 180) % 360}, 40%, 15%))\`;
                }, 300);
            });
        });
    }

    revealDiscovery() {
        const mysteryBox = document.getElementById('mysteryBox');
        const resultsArea = document.getElementById('discoveryResults');
        
        mysteryBox.style.transform = 'scale(0.8) rotateY(180deg)';
        
        setTimeout(() => {
            const discoveries = [
                { type: 'fact', content: \`Unique timestamp: \${this.timestamp}\` },
                { type: 'feature', content: 'Hidden interactive element unlocked!' },
                { type: 'surprise', content: 'You discovered a secret animation!' },
                { type: 'bonus', content: 'Special color theme activated!' }
            ];
            
            const randomDiscovery = discoveries[Math.floor(Math.random() * discoveries.length)];
            
            resultsArea.innerHTML = \`
                <div class="discovery-reveal">
                    <h4>🎉 Discovery!</h4>
                    <p>\${randomDiscovery.content}</p>
                    <button onclick="app.createSpecialEffect()" class="special-btn">Activate</button>
                </div>
            \`;
            
            mysteryBox.style.transform = 'scale(1) rotateY(0deg)';
        }, 300);
    }

    createSpecialEffect() {
        document.body.style.animation = 'specialGlow 2s ease-in-out';
        setTimeout(() => {
            document.body.style.animation = '';
        }, 2000);
    }

    handleKeyboard(e) {
        const shortcuts = {
            'Space': () => this.handlePrimaryAction({ target: document.getElementById('primaryAction') }),
            'KeyC': () => this.generateCreativeContent(document.getElementById('dynamicArea')),
            'KeyE': () => this.generateExplorationInterface(document.getElementById('dynamicArea')),
            'KeyD': () => this.generateDiscoveryExperience(document.getElementById('dynamicArea'))
        };

        if (shortcuts[e.code]) {
            e.preventDefault();
            shortcuts[e.code]();
        }
    }

    handleMouseMove(e) {
        // Subtle parallax effect
        const elements = document.querySelectorAll('.feature-card');
        const x = (e.clientX / window.innerWidth) * 2 - 1;
        const y = (e.clientY / window.innerHeight) * 2 - 1;

        elements.forEach((el, index) => {
            const intensity = (index + 1) * 0.5;
            el.style.transform = \`translate(\${x * intensity}px, \${y * intensity}px)\`;
        });
    }

    handleResize() {
        // Responsive adjustments
        console.log('Responsive adjustment triggered');
    }

    startDynamicAnimations() {
        // Continuous subtle animations
        setInterval(() => {
            const decorations = document.querySelectorAll('.header-decoration, .footer-decoration');
            decorations.forEach(el => {
                el.style.background = \`linear-gradient(90deg, hsl(\${(Date.now() / 50) % 360}, 70%, 60%), hsl(\${(Date.now() / 50 + 60) % 360}, 65%, 55%))\`;
            });
        }, 100);
    }

    initializeUniqueFeatures() {
        // Add unique CSS animations
        const style = document.createElement('style');
        style.textContent = \`
            @keyframes particleRise {
                to {
                    transform: translateY(-100vh) rotate(360deg);
                    opacity: 0;
                }
            }

            @keyframes specialGlow {
                0%, 100% { filter: hue-rotate(0deg); }
                50% { filter: hue-rotate(180deg) brightness(1.2); }
            }

            .unique-particle {
                opacity: 1;
            }

            .exploration-card {
                animation: fadeInUp 0.6s ease-out;
                cursor: pointer;
                transition: all 0.3s ease;
            }

            .exploration-card:hover {
                transform: translateY(-5px) scale(1.02);
            }

            .creative-workspace {
                text-align: center;
            }

            .creation-tools {
                margin: 1rem 0;
            }

            .tool-btn {
                margin: 0 0.5rem;
                padding: 0.5rem 1rem;
                border: none;
                background: rgba(255, 255, 255, 0.1);
                color: white;
                border-radius: 8px;
                cursor: pointer;
                transition: all 0.3s ease;
            }

            .tool-btn:hover {
                background: var(--primary-color);
                transform: translateY(-2px);
            }

            .canvas-area {
                margin: 1rem 0;
                display: flex;
                justify-content: center;
            }

            #uniqueCanvas {
                border: 2px solid rgba(255, 255, 255, 0.2);
                border-radius: 8px;
                background: rgba(255, 255, 255, 0.05);
                cursor: crosshair;
            }

            .color-palette {
                display: flex;
                justify-content: center;
                gap: 0.5rem;
                margin-top: 1rem;
            }

            .color-swatch {
                width: 30px;
                height: 30px;
                border-radius: 50%;
                cursor: pointer;
                border: 2px solid transparent;
                transition: all 0.3s ease;
            }

            .color-swatch:hover,
            .color-swatch.active {
                border-color: white;
                transform: scale(1.2);
            }

            .discovery-reveal {
                text-align: center;
                padding: 2rem;
                background: rgba(255, 255, 255, 0.1);
                border-radius: 12px;
                animation: fadeInUp 0.6s ease-out;
            }

            .special-btn {
                background: linear-gradient(135deg, var(--accent-color), var(--secondary-color));
                color: white;
                border: none;
                padding: 0.8rem 2rem;
                border-radius: 25px;
                font-weight: 600;
                cursor: pointer;
                margin-top: 1rem;
                transition: all 0.3s ease;
            }

            .special-btn:hover {
                transform: translateY(-3px);
                box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
            }
        \`;
        document.head.appendChild(style);
    }

    updateDynamicStats() {
        // Update stats with unique values
        const stats = document.querySelectorAll('.stat-value');
        stats.forEach((stat, index) => {
            if (index === 0) return; // Skip interaction counter
            
            const currentValue = parseInt(stat.textContent) || 0;
            const newValue = currentValue + Math.floor(Math.random() * 10) + 1;
            
            stat.style.transform = 'scale(1.2)';
            setTimeout(() => {
                stat.textContent = newValue;
                stat.style.transform = 'scale(1)';
            }, 200);
        });
    }
}

// Initialize the unique application
const app = new UniqueApp();

// Global functions for HTML onclick events
window.app = app;

// Log unique app information
console.log(\`
🎨 Unique AI Generated Application
📝 Description: ${desc}
🔢 Build ID: \${app.timestamp}
⚡ Features: Interactive, Creative, Responsive
🎯 Purpose: Demonstrate AI-powered unique app creation
\`);

// Welcome message
setTimeout(() => {
    console.log('✨ Welcome to your unique AI-generated application! Try the interactive features.');
}, 1000);`;
  };

  // Get AI status for display
  const getAIStatus = () => {
    if (settings.aiProvider === 'local') {
      const runningModel = localStorage.getItem('runningLocalModel');
      const engineReady = !!window.localLLMEngine;
      if (runningModel && engineReady) {
        return `Local: ${runningModel} (Ready)`;
      } else if (runningModel) {
        return `Local: ${runningModel} (Loading...)`;
      } else {
        return 'Local model not loaded';
      }
    }
    if (settings.aiProvider === 'openai') {
      return settings.aiApiKey ? 'OpenAI: Connected' : 'OpenAI: No API key';
    }
    if (settings.aiProvider === 'anthropic') {
      return settings.aiApiKey ? 'Claude: Connected' : 'Claude: No API key';
    }
    if (settings.aiProvider === 'custom') {
      return settings.aiApiKey && settings.customApiUrl ? 'Custom: Connected' : 'Custom: Not configured';
    }
    return 'Not configured';
  };

  if (!isAIConfigured()) {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50">
        <motion.div
          className="bg-gradient-to-br from-gray-900 to-gray-800 border border-gray-700 rounded-2xl p-8 w-full max-w-md shadow-2xl"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <div className="text-center">
            <SafeIcon icon={FiAlertCircle} className="text-6xl text-yellow-400 mb-4 mx-auto" />
            <h3 className="text-xl font-bold mb-4 text-white">AI Not Configured</h3>
            <div className="mb-4 p-3 bg-gray-800/50 rounded-lg">
              <p className="text-sm text-gray-400 mb-2">Current Status:</p>
              <p className="text-white font-medium">{getAIStatus()}</p>
            </div>
            <p className="text-gray-400 mb-6">
              {settings.aiProvider === 'local'
                ? 'Please load a local model in Settings > AI Configuration > Manage Models. Recommended: TinyLlama, RedPajama 3B, or Llama 2 7B.'
                : 'Please configure your AI API key in Settings > AI Configuration'}
            </p>
            <button
              onClick={onClose}
              className="w-full bg-gray-600 hover:bg-gray-700 px-4 py-3 rounded-lg font-semibold transition-colors duration-200"
            >
              Close
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50">
      <motion.div
        className="bg-gradient-to-br from-gray-900 to-gray-800 border border-gray-700 rounded-2xl p-8 w-full max-w-2xl shadow-2xl"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-bold text-white flex items-center">
            <SafeIcon icon={BiBot} className="mr-3 text-purple-400" />
            Create Unique App with AI
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <SafeIcon icon={FiX} className="text-xl" />
          </button>
        </div>

        {/* AI Status Display */}
        <div className="mb-6 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
          <div className="flex items-center justify-between">
            <span className="text-green-300 text-sm font-medium">AI Ready:</span>
            <span className="text-green-400 font-semibold">{getAIStatus()}</span>
          </div>
        </div>

        <div className="space-y-6">
          <div>
            <label className="block text-sm font-semibold mb-3 text-gray-300">
              Project Name
            </label>
            <input
              type="text"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg focus:border-purple-500 focus:outline-none text-white"
              placeholder="My Amazing AI App"
              disabled={loading}
            />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-3 text-gray-300">
              Describe Your Unique App Vision
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg focus:border-purple-500 focus:outline-none text-white h-40 resize-none"
              placeholder="Be creative and specific! Examples:

• 'A mystical potion brewing simulator with bubbling animations, ingredient mixing, and magical particle effects'
• 'An interactive galaxy explorer with rotating planets, shooting stars, and clickable celestial bodies'
• 'A creative music visualizer that responds to mouse movement with dancing geometric shapes and color waves'
• 'A virtual garden designer with drag-and-drop plants, seasonal changes, and growth animations'
• 'An abstract art generator that creates unique patterns based on user gestures and color preferences'

The more unique and creative your vision, the more amazing your app will be!"
              disabled={loading}
            />
          </div>

          <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
            <div className="flex items-start">
              <SafeIcon icon={FiZap} className="text-purple-400 mr-2 mt-1 flex-shrink-0" />
              <div className="text-sm text-purple-200">
                <p className="font-semibold mb-2">🎨 Enhanced AI App Generator - UNIQUE Mode</p>
                <ul className="list-disc list-inside space-y-1 text-purple-300">
                  <li>Creates completely unique applications every time (no templates!)</li>
                  <li>Uses timestamp and random seeds to ensure uniqueness</li>
                  <li>Generates creative HTML with innovative structure and features</li>
                  <li>Beautiful CSS with unique animations, gradients, and effects</li>
                  <li>Interactive JavaScript with creative functionality and engaging UX</li>
                  <li>Professional-quality code with comments and best practices</li>
                  <li>✅ <strong>FIXED:</strong> Now properly saves all files to Supabase!</li>
                </ul>
                <p className="mt-2 text-xs text-purple-400">
                  💡 Pro Tip: Be very specific about interactions, visual style, colors, and unique features for best results!
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex gap-4 mt-8">
          <button
            onClick={handleCreateApp}
            disabled={!projectName.trim() || !description.trim() || loading}
            className="flex-1 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 disabled:from-gray-700 disabled:to-gray-800 disabled:text-gray-500 px-6 py-3 rounded-xl font-semibold transition-all duration-200 flex items-center justify-center"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                {settings.aiProvider === 'local' ? 'AI Creating Unique Magic...' : 'Generating Your Unique App...'}
              </>
            ) : (
              <>
                <SafeIcon icon={BiBot} className="mr-2" />
                Create Unique App with AI
              </>
            )}
          </button>
          <button
            onClick={onClose}
            disabled={loading}
            className="px-6 py-3 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 rounded-xl font-semibold transition-colors duration-200"
          >
            Cancel
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default AIAppCreator;